import type { JSX, SyntheticEvent } from "react";
import { useEffect, useRef, useState } from "react";
import { Check, Copy, ExternalLink } from "lucide-react";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tip } from "@/components/InfoTip";
import { cn } from "@/lib/utils";

import dpmlolIcon from "@/assets/profile-sites/dpmlol.png";
import opggIcon from "@/assets/profile-sites/opgg.png";
import uggIcon from "@/assets/profile-sites/ugg.png";

/** op.gg region slug per Riot platform id (op.gg doesn't use platform ids). */
const OPGG_REGION_BY_PLATFORM: Record<string, string> = {
  euw1: "euw",
  eun1: "eune",
  na1: "na",
  kr: "kr",
  br1: "br",
  jp1: "jp",
  la1: "lan",
  la2: "las",
  oc1: "oce",
  tr1: "tr",
  ru: "ru",
  me1: "me",
  ph2: "ph",
  sg2: "sg",
  th2: "th",
  tw2: "tw",
  vn2: "vn"
};

export type LeagueProfileLink = { label: string; url: string; iconUrl: string };

/**
 * Stats-site profile links for a Riot ID on the match's platform (u.gg /
 * op.gg / dpm.lol). Empty without a `#TAG`; dpm.lol resolves Riot IDs
 * globally so it works even when the platform is unknown.
 *
 * Same URL rules as the app's `leagueProfileLinks` — the website builds these
 * at hover time from name/tag/platform, nothing is stored in the sidecar.
 */
const leagueProfileLinks = (
  platformId: string | null | undefined,
  name: string,
  tag: string
): LeagueProfileLink[] => {
  if (!name || !tag) return [];
  const platform = (platformId ?? "").trim().toLowerCase();
  const slug = `${encodeURIComponent(name)}-${encodeURIComponent(tag)}`;
  const links: LeagueProfileLink[] = [];
  const opggRegion = OPGG_REGION_BY_PLATFORM[platform];
  if (opggRegion) {
    links.push({
      label: "op.gg",
      url: `https://op.gg/summoners/${opggRegion}/${slug}`,
      iconUrl: opggIcon
    });
  }
  links.push({
    label: "dpm.lol",
    url: `https://dpm.lol/${slug}`,
    iconUrl: dpmlolIcon
  });
  if (platform) {
    links.push({
      label: "u.gg",
      url: `https://u.gg/lol/profile/${platform}/${slug}/overview`,
      iconUrl: uggIcon
    });
  }
  return links;
};

const hideBrokenImage = (event: SyntheticEvent<HTMLImageElement>): void => {
  event.currentTarget.style.visibility = "hidden";
};

const riotHoverButtonClass = cn(
  "inline-flex size-5 items-center justify-center rounded-full bg-transparent text-muted-foreground",
  "opacity-0 transition-[opacity,background-color,color] group-hover/riot:opacity-100 focus-visible:opacity-100",
  "hover:bg-foreground/10 hover:text-foreground focus-visible:bg-foreground/10 focus-visible:ring-2 focus-visible:ring-ring/50"
);

/** Hover-to-reveal flyout listing stats sites that open in a new tab. */
const ProfileLinksButton = ({ links }: { links: LeagueProfileLink[] }): JSX.Element => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const cancelClose = (): void => {
    if (closeTimer.current != null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  // Grace period so the pointer can cross the gap into the flyout.
  const scheduleClose = (): void => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setOpen(false), 150);
  };

  useEffect(() => cancelClose, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <span
        className="inline-flex shrink-0 self-center"
        onMouseEnter={() => {
          cancelClose();
          setOpen(true);
        }}
        onMouseLeave={scheduleClose}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label="Open profile on a stats site"
            onClick={(event) => event.stopPropagation()}
            className={cn(riotHoverButtonClass, "data-[state=open]:opacity-100")}
          >
            <ExternalLink className="size-3" />
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="top"
          align="center"
          sideOffset={4}
          className="flex w-auto min-w-26 flex-col gap-0.5 p-1"
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          {links.map((link) => (
            <a
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-xs font-medium hover:bg-accent hover:text-accent-foreground"
            >
              <img
                src={link.iconUrl}
                alt=""
                draggable={false}
                loading="lazy"
                decoding="async"
                onError={hideBrokenImage}
                className="size-3.5 shrink-0 rounded-sm"
              />
              {link.label}
              <ExternalLink className="ml-auto size-2.5 text-muted-foreground" />
            </a>
          ))}
        </PopoverContent>
      </span>
    </Popover>
  );
};

/**
 * Player Riot ID with hover-to-copy and optional stats-site flyout — same chrome
 * as the app Match Details row.
 */
export const ClipRiotIdLabel = ({
  name,
  tag,
  platformId,
  isLocal
}: {
  name: string;
  tag?: string | null;
  platformId?: string | null;
  isLocal?: boolean;
}): JSX.Element => {
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);
  const riotId = tag ? `${name}#${tag}` : name;
  const profileLinks = leagueProfileLinks(platformId, name, tag ?? "");

  useEffect(
    () => () => {
      if (copiedTimer.current != null) window.clearTimeout(copiedTimer.current);
    },
    []
  );

  const copyRiotId = (): void => {
    if (!riotId) return;
    void navigator.clipboard.writeText(riotId).then(() => {
      setCopied(true);
      if (copiedTimer.current != null) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopied(false), 1200);
    });
  };

  return (
    <span className="group/riot flex min-w-0 items-center gap-1">
      <span
        className={cn(
          "min-w-0 truncate text-xs",
          isLocal ? "font-bold text-foreground" : "text-muted-foreground"
        )}
      >
        {name}
        {tag ? <span className="text-muted-foreground/60"> #{tag}</span> : null}
      </span>
      <Tip label="Copy Riot ID" side="top" className="inline-flex shrink-0 self-center">
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            copyRiotId();
          }}
          aria-label={copied ? "Copied Riot ID" : "Copy Riot ID"}
          className={riotHoverButtonClass}
        >
          {copied ? <Check className="size-3 text-success" /> : <Copy className="size-3" />}
        </button>
      </Tip>
      {profileLinks.length > 0 ? <ProfileLinksButton links={profileLinks} /> : null}
    </span>
  );
};
